// Oile Home product page behaviour:
// 1. Size-selection guard — when a product has a size option (theme setting "Ölçü seçeneği adları") and the page
//    was opened without ?variant=, the customer must pick a size before adding to cart (also in quick add).
//    While pending: add-to-cart is aria-disabled, the variant id input is disabled and accelerated checkout is blocked.
// 2. Badge refresh on variant change (sale badge follows the selected variant).
// 3. Sticky add-to-cart (mobile only) mirroring the main button; never adds while a size choice is pending.
// 4. Native <dialog> opener/closer (size guide).
// Quick add re-injects this script, so document-level wiring runs once.

(() => {
  const SIZE_FIELDSET = '[data-oile-size]';

  function sectionIdOf(info) {
    return info.dataset.originalSection || info.dataset.section;
  }

  function submitButtonOf(info) {
    return info.querySelector('[id^="ProductSubmitButton-"]');
  }

  function setPending(info, pending) {
    const fieldset = info.querySelector(SIZE_FIELDSET);
    const button = submitButtonOf(info);
    const idInputs = info.querySelectorAll('product-form input[name="id"]');
    info.toggleAttribute('data-oile-size-pending', pending);

    if (pending) {
      idInputs.forEach((input) => (input.disabled = true));
      if (button) {
        button.removeAttribute('disabled');
        button.setAttribute('aria-disabled', 'true');
        const label = button.querySelector('span');
        if (label && fieldset?.dataset.cta) label.textContent = fieldset.dataset.cta;
      }
    } else {
      idInputs.forEach((input) => (input.disabled = false));
      button?.removeAttribute('aria-disabled');
      info.querySelector('[data-oile-size-hint]')?.setAttribute('hidden', '');
    }
  }

  function initGuard(info) {
    if (!info || !info.hasAttribute('data-oile-require-size') || info.oileGuard) return;
    const state = { picked: false };
    info.oileGuard = state;

    // Capture phase: runs before Dawn's variant-selects handler publishes the change.
    info.addEventListener(
      'change',
      (event) => {
        if (event.target.closest(SIZE_FIELDSET) && !state.picked) {
          state.picked = true;
          setPending(info, false);
        }
      },
      true
    );

    applyGuard(info);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId === sectionIdOf(info) && info.isConnected) applyGuard(info);
      });
    }
  }

  function applyGuard(info) {
    const state = info.oileGuard;
    const fieldset = info.querySelector(SIZE_FIELDSET);
    if (!state || !fieldset || state.picked) return;

    fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    const selected = fieldset.querySelector('[data-selected-value]');
    if (selected) selected.textContent = fieldset.dataset.placeholder || '';
    setPending(info, true);
  }

  function focusSizeChoice(info) {
    const fieldset = info.querySelector(SIZE_FIELDSET);
    if (!fieldset) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    fieldset.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    info.querySelector('[data-oile-size-hint]')?.removeAttribute('hidden');
    const first =
      fieldset.querySelector('input[type="radio"]:not(.disabled)') || fieldset.querySelector('input[type="radio"]');
    first?.focus({ preventScroll: true });
  }

  function initInfo(info) {
    initGuard(info);
  }

  // Guard product-info elements present now (runs on every injection, cheap and idempotent).
  document.querySelectorAll('product-info').forEach(initInfo);

  if (window.__oileProductInit) return;
  window.__oileProductInit = true;

  document.addEventListener('product-info:loaded', (event) => initInfo(event.target));
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('product-info')) initInfo(node);
        node.querySelectorAll?.('product-info').forEach(initInfo);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });

  // Badges follow the selected variant (Dawn only swaps price/inventory/sku).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.html) return;
      document.querySelectorAll(`product-info`).forEach((info) => {
        if (sectionIdOf(info) !== data.sectionId) return;
        const current = info.querySelector('input[name="id"]')?.value;
        if (data.variant && current && String(data.variant.id) !== String(current)) return;
        const target = info.querySelector(`#Badges-${info.dataset.section}`);
        const source = data.html.getElementById(`Badges-${data.sectionId}`);
        if (target && source) target.innerHTML = source.innerHTML;
      });
    });
  }

  // ---------- Sticky add to cart (mobile) ----------
  if (!customElements.get('oile-sticky-atc')) {
    const mobile = window.matchMedia('(max-width: 989px)');

    customElements.define(
      'oile-sticky-atc',
      class OileStickyAtc extends HTMLElement {
        connectedCallback() {
          if (this.closest('quick-add-modal')) return;
          this.info = this.closest('product-info');
          this.mainButton = this.info && submitButtonOf(this.info);
          this.button = this.querySelector('[data-sticky-button]');
          this.priceEl = this.querySelector('[data-sticky-price]');
          this.variantEl = this.querySelector('[data-sticky-variant]');
          if (!this.info || !this.mainButton || !this.button) return;

          this.button.addEventListener('click', () => this.onClick());
          this.syncButton();

          this.buttonObserver = new MutationObserver(() => this.syncButton());
          this.observeButton();
          this.pendingObserver = new MutationObserver(() => this.syncButton());
          this.pendingObserver.observe(this.info, { attributes: true, attributeFilter: ['data-oile-size-pending'] });

          this.io = new IntersectionObserver(([entry]) => {
            this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.updateVisibility();
          });
          this.io.observe(this.mainButton);
          this.onMedia = () => this.updateVisibility();
          mobile.addEventListener('change', this.onMedia);

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.onVariantChange(data));
          }
        }

        disconnectedCallback() {
          this.io?.disconnect();
          this.buttonObserver?.disconnect();
          this.pendingObserver?.disconnect();
          this.unsubscribe?.();
          mobile.removeEventListener('change', this.onMedia);
          if (this.ownsBodyClass) document.body.classList.remove('oile-sticky-atc-visible');
        }

        observeButton() {
          this.buttonObserver.disconnect();
          this.buttonObserver.observe(this.mainButton, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        updateVisibility() {
          const show = Boolean(this.pastButton) && mobile.matches;
          this.toggleAttribute('hidden', !show);
          document.body.classList.toggle('oile-sticky-atc-visible', show);
          this.ownsBodyClass = show;
        }

        onVariantChange(data) {
          if (!data || data.sectionId !== sectionIdOf(this.info)) return;
          const current = this.info.querySelector('input[name="id"]')?.value;
          if (data.variant && current && String(data.variant.id) !== String(current)) return; // another form (quick add)

          const fresh = submitButtonOf(this.info);
          if (fresh && fresh !== this.mainButton) {
            this.mainButton = fresh;
            this.observeButton();
          }
          if (this.variantEl && data.variant) this.variantEl.textContent = data.variant.title || '';
          const priceRoot = this.info.querySelector(`#price-${this.info.dataset.section}`);
          const onSale = priceRoot?.querySelector('.price--on-sale');
          const el = onSale
            ? priceRoot.querySelector('.price__sale .price-item--last')
            : priceRoot?.querySelector('.price__regular .price-item--regular');
          const text = el?.textContent.trim();
          if (this.priceEl && text) this.priceEl.textContent = text;
          this.syncButton();
        }

        syncButton() {
          const pending = this.info.hasAttribute('data-oile-size-pending');
          this.button.disabled = !pending && this.mainButton.hasAttribute('disabled');
          const label = this.mainButton.querySelector('span')?.textContent.trim();
          if (label) this.button.textContent = label;
          if (this.variantEl && pending) {
            this.variantEl.textContent = this.info.querySelector(SIZE_FIELDSET)?.dataset.cta || '';
          }
        }

        onClick() {
          if (this.info.hasAttribute('data-oile-size-pending')) {
            focusSizeChoice(this.info);
            return;
          }
          if (this.mainButton.hasAttribute('disabled')) return;
          this.mainButton.click();
        }
      }
    );
  }

  // ---------- Dialogs ----------
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-oile-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.oileDialogOpen);
      if (dialog?.showModal && !dialog.open) {
        dialog.showModal();
        dialog.oileOpener = opener;
      }
      return;
    }
    const closer = event.target.closest('[data-oile-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('oile-dialog')) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement) event.target.oileOpener?.focus();
    },
    true
  );

  // Keep Escape inside an Oile dialog from also closing surrounding Dawn modals.
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.code === 'Escape' && event.target.closest?.('dialog.oile-dialog')) event.stopPropagation();
    },
    true
  );

  // "Sepete Ekle" while a size choice is pending guides the customer to the size options.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('[id^="ProductSubmitButton-"]');
      if (!button) return;
      const info = button.closest('product-info');
      if (info?.hasAttribute('data-oile-size-pending')) {
        event.preventDefault();
        event.stopPropagation();
        focusSizeChoice(info);
      }
    },
    true
  );
})();
